import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../firebase";
import { getNextZ } from "../utils/zIndexManager";
import "./modalStyles.css";
import { formatRarityName } from "../utils/formatRarityName";

const rarityWeight = {
    'legendary': 5,
    'epic': 4,
    'rare': 3,
    'uncommon': 2,
    'common': 1
  };

export default function PersonalAssetsModal({ currentUser, onClose }) {
  const [databaseItems, setDatabaseItems] = useState([]);
  const [userItems, setUserItems] = useState({});
  
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyOwned, setShowOnlyOwned] = useState(false);
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);
  
  const [listingItem, setListingItem] = useState(null);
  const [listQty, setListQty] = useState(1);
  const [listPrice, setListPrice] = useState("");

  const modalRef = useRef(null);
  const [drag, setDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [position, setPosition] = useState({ top: 100, left: 100 });

const getRarityColor = (rarity) => {
  switch (rarity?.toLowerCase()) {
    case 'legendary': return '#ffb100';
    case 'epic': return '#a335ee';
    case 'rare': return '#0070dd';
    case 'uncommon': return '#1eff00';
    default: return '#888888';
  }
};

useEffect(() => {
  const closeMenu = () => setIsSortOpen(false);
  window.addEventListener("click", closeMenu);
  return () => window.removeEventListener("click", closeMenu);
}, []);

const sortOptions = [
  { label: "DEFAULT", value: "default" },
  { label: "QUANTITY", value: "qty" },
  { label: "RARITY DESC", value: "rarity-down" },
  { label: "RARITY ASC", value: "rarity-up" },
  { label: "NAME A-Z", value: "name-asc" },
  { label: "NAME Z-A", value: "name-desc" }
];

  useEffect(() => {
    const q = query(collection(db, "Item_Database"));
    const unsub = onSnapshot(q, (snap) => {
      setDatabaseItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const userItemsRef = collection(db, "Users", currentUser.uid, "Items");
    const unsub = onSnapshot(userItemsRef, (snap) => {
      const owned = {};
      snap.forEach((doc) => { owned[doc.id] = doc.data(); });
      setUserItems(owned);
    });
    return () => unsub();
  }, [currentUser]);

const sortedDisplay = useMemo(() => {
  let filtered = [...databaseItems];

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(item => 
      item.name.toLowerCase().includes(term) ||
      item.id.toLowerCase().includes(term)
    );
  }

  if (showOnlyOwned) {
    filtered = filtered.filter(item => !!userItems[item.id]);
  }

  return filtered.sort((a, b) => {
    const weightA = rarityWeight[a.rarity?.toLowerCase()] || 0;
    const weightB = rarityWeight[b.rarity?.toLowerCase()] || 0;
    const qtyA = userItems[a.id]?.quantity || 0;
    const qtyB = userItems[b.id]?.quantity || 0;

    switch (sortBy) {
      case "qty":
        return qtyB - qtyA || a.name.localeCompare(b.name);

      case "name-asc":
        return a.name.localeCompare(b.name);

      case "name-desc":
        return b.name.localeCompare(a.name);

      case "rarity-up":
        return weightA - weightB || a.name.localeCompare(b.name);

      case "rarity-down":
      case "default":
      default:
        return weightB - weightA || a.name.localeCompare(b.name);
    }
  });
}, [databaseItems, userItems, sortBy, showOnlyOwned, searchTerm]);

  const handleSellTransition = (item, ownedData) => {
    setListingItem({ item, ownedData });
    setListQty(1);
    setListPrice("");
  };

  const submitListing = async () => {
    if (!listPrice || listPrice <= 0 || !listQty || listQty <= 0) {
      alert("Please enter a valid price and quantity.");
      return;
    }
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch('https://us-central1-stockmarket-83a73.cloudfunctions.net/listAssetOnExchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ itemId: listingItem.item.id, quantity: listQty, price: parseFloat(listPrice) })
      });
      const result = await response.json();
      if (result.success) { setListingItem(null); setListQty(1); setListPrice(""); } 
      else { alert(`Listing Failed: ${result.message}`); }
    } catch (err) { alert("A system error occurred."); }
  };

  useEffect(() => {
    const { innerWidth, innerHeight } = window;
    setPosition({ top: innerHeight / 2 - 250, left: innerWidth / 2 - 260 });
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
    setZIndex(getNextZ() + 10000);
    const rect = modalRef.current.getBoundingClientRect();
    setDrag({ isDragging: true, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top });
  };

  const handleMouseMove = useCallback((e) => {
    if (!drag.isDragging) return;
    setPosition({ top: e.clientY - drag.offsetY, left: e.clientX - drag.offsetX });
  }, [drag.isDragging, drag.offsetY, drag.offsetX]);

  const handleMouseUp = useCallback(() => setDrag(prev => ({ ...prev, isDragging: false })), []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const modalContent = (
<div
      ref={modalRef}
      className="floating-modal-container"
      style={{
        position: "fixed", top: `${position.top}px`, left: `${position.left}px`,
        width: "520px", zIndex, fontFamily: "'JetBrains Mono', monospace",
        border: "1px solid #50E3C2", backgroundColor: "#050505", boxShadow: "0 0 20px rgba(0,0,0,0.8)"
      }}
    >
      <div 
        className="draggable-title" 
        onMouseDown={handleMouseDown}
        style={{ cursor: 'drag' }}
      >
        <span>{listingItem ? "SELL ASSET" : "ASSET DATABASE"}</span>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>
      </div>

      {!listingItem && (
        <>

<div style={{ 
  padding: '12px', 
  background: 'rgba(80, 227, 194, 0.02)', 
  borderBottom: '1px solid #222',
  position: 'relative' 
}}>
  
  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
    <input 
      type="text" 
      placeholder="SEARCH"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      style={{
        width: '100%', 
        background: '#0a0a0a', 
        border: '1px solid #333',
        borderLeft: `2px solid #50E3C2`,
        color: '#50E3C2', 
        padding: '10px 12px', 
        fontSize: '0.75rem', 
        outline: 'none',
        fontFamily: "'JetBrains Mono', monospace",
        transition: 'all 0.2s ease',
        boxShadow: 'inset 0 0 5px rgba(0,0,0,0.5)'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#50E3C2';
        e.target.style.boxShadow = '0 0 10px rgba(80, 227, 194, 0.2)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#333';
        e.target.style.boxShadow = 'inset 0 0 5px rgba(0,0,0,0.5)';
      }}
    />
  </div>
</div>

          <div style={{ 
            padding: '10px 12px', background: 'rgba(80, 227, 194, 0.05)', borderBottom: '1px solid #333', 
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.6rem' 
          }}>
<div style={{ display: 'flex', gap: '15px' }}>
  <div 
    onClick={() => setShowOnlyOwned(false)} 
    style={{ 
      cursor: 'pointer', 
      color: !showOnlyOwned ? '#50E3C2' : '#666', 
      borderBottom: !showOnlyOwned ? '1px solid #50E3C2' : 'none' 
    }}
  >
    DATABASE ({databaseItems.length})
  </div>
  
  <div 
    onClick={() => setShowOnlyOwned(true)} 
    style={{ 
      cursor: 'pointer', 
      color: showOnlyOwned ? '#50E3C2' : '#666', 
      borderBottom: showOnlyOwned ? '1px solid #50E3C2' : 'none' 
    }}
  >
    OWNED ({Object.keys(userItems).length})
  </div>
</div>

<div style={{ 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'stretch', 
  background: '#111', 
  borderBottom: '1px solid #50E3C233', 
  height: '40px'
}}>

  <div 
    onClick={(e) => { e.stopPropagation(); setIsSortOpen(!isSortOpen); }}
    style={{ 
      display: "flex", 
      flexDirection: "column", 
      background: isSortOpen ? "rgba(80, 227, 194, 0.1)" : "rgba(0,0,0,0.3)", 
      padding: "4px 12px", 
      borderLeft: "2px solid #50E3C2", 
      cursor: "pointer", 
      position: "relative",
      minWidth: "140px",
      justifyContent: "center"
    }}
  >
    <label style={{ color: "#666", fontSize: "0.45rem", marginBottom: "2px", letterSpacing: "1px" }}>
      SORT
    </label>
    <div style={{ color: "#50E3C2", fontSize: "0.65rem", display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
      {sortOptions.find(o => o.value === sortBy)?.label} 
      <span style={{ marginLeft: '8px' }}>{isSortOpen ? "▴" : "▾"}</span>
    </div>

    {isSortOpen && (
      <div style={{ 
        position: "absolute", 
        top: "100%", 
        right: -1,
        width: "100.5%", 
        zIndex: 100, 
        background: "#050505", 
        border: "1px solid #50E3C2", 
        borderTop: "none",
        boxShadow: "0 10px 30px rgba(0,0,0,0.9)"
      }}>
        {sortOptions.map(opt => (
          <div 
            key={opt.value} 
            onClick={() => { setSortBy(opt.value); setIsSortOpen(false); }}
            style={{ 
              padding: "10px 12px", 
              color: sortBy === opt.value ? "#000" : "#50E3C2", 
              background: sortBy === opt.value ? "#50E3C2" : "transparent",
              fontSize: "0.6rem", 
              borderBottom: "1px solid rgba(80, 227, 194, 0.1)",
              transition: "all 0.1s ease"
            }}
            onMouseEnter={(e) => {
              if (sortBy !== opt.value) e.target.style.background = "rgba(80, 227, 194, 0.1)";
            }}
            onMouseLeave={(e) => {
              if (sortBy !== opt.value) e.target.style.background = "transparent";
            }}
          >
            {opt.label}
          </div>
        ))}
      </div>
    )}
  </div>
</div>
          </div>
        </>
      )}

      <div className="scrollable-content" style={{ maxHeight: '450px', padding: '15px', overflowY: 'auto' }}>
        {listingItem ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', borderBottom: '1px solid #222', paddingBottom: '15px' }}>
              <div style={{ width: '80px', height: '80px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(255,255,255,0.02)', border: `1px solid ${getRarityColor(listingItem.item.rarity)}33` }}>
                <img 
                  src={`/items/${listingItem.item.id}.png`}
                  alt={listingItem.item.name}
                  style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain', filter: `drop-shadow(0 0 5px ${getRarityColor(listingItem.item.rarity)})` }}
                />
              </div>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#fff' }}>{listingItem.item.name}</div>
                <div style={{ fontSize: '0.6rem', color: getRarityColor(listingItem.item.rarity), textTransform: 'uppercase', fontWeight: 'bold' }}>
                  {formatRarityName(listingItem.item.rarity)} / {listingItem.ownedData.quantity} OWNED
                </div>
              </div>
            </div>

            {/* Inputs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {listingItem.ownedData.quantity > 1 && (
                <div>
                  <label style={{ fontSize: '0.6rem', color: '#888', display: 'block', marginBottom: '8px' }}>QUANTITY TO SELL</label>
                  <input 
                    type="number" min="1" max={listingItem.ownedData.quantity}
                    value={listQty}
                    onChange={(e) => setListQty(Math.min(listingItem.ownedData.quantity, Math.max(1, parseInt(e.target.value) || 1)))}
                    style={{ width: '100%', background: '#000', border: '1px solid #333', color: '#50E3C2', padding: '10px', fontFamily: 'inherit', outline: 'none' }}
                  />
                </div>
              )}
              <div>
                <label style={{ fontSize: '0.6rem', color: '#888', display: 'block', marginBottom: '8px' }}>ASKING PRICE (PER UNIT)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '10px', top: '10px', color: '#50E3C2' }}>$</span>
                  <input 
                    type="number" placeholder="0.00"
                    value={listPrice}
                    onChange={(e) => setListPrice(e.target.value)}
                    style={{ width: '92.5%', background: '#111', border: `1px solid ${getRarityColor(listingItem.item.rarity)}`, color: '#fff', padding: '10px 10px 10px 25px', fontFamily: 'inherit', outline: 'none' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={() => setListingItem(null)} style={{ flex: 1, background: 'transparent', border: '1px solid #444', color: '#666', padding: '10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.7rem' }}>CANCEL</button>
              <button onClick={submitListing} disabled={!listPrice || listPrice <= 0} style={{ flex: 2, background: (listPrice > 0) ? '#50E3C2' : '#222', border: 'none', color: '#000', padding: '10px', cursor: (listPrice > 0) ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontFamily: 'inherit', fontSize: '0.7rem' }}>CONFIRM LISTING</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px' }}>
            {sortedDisplay.map((item) => {
              const owned = userItems[item.id];
              const rarityColor = getRarityColor(item.rarity);
              return (
                <div key={item.id} style={{
                  border: `1px solid ${owned ? rarityColor : '#222'}`, padding: '12px', textAlign: 'center',
                  background: owned ? `${rarityColor}08` : 'transparent', opacity: owned ? 1 : 0.25,
                  position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                }}>
                  {owned && (
                    <div style={{ position: 'absolute', top: 0, right: 0, background: rarityColor, color: '#000', fontSize: '0.5rem', padding: '1px 4px', fontWeight: 'bold' }}>
                      x{owned.quantity || 1}
                    </div>
                  )}
                  <div style={{ width: '100%', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '8px' }}>
                    <img 
                      src={`/items/${item.id}.png`} alt={item.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', filter: owned ? `drop-shadow(0 0 3px ${rarityColor}aa)` : 'none' }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement.innerHTML = `<span style="font-size: 1.4rem; color: ${rarityColor};">${item.rarity === 'Legendary' ? '✦' : '⬡'}</span>`;
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: owned ? '#fff' : '#fff' }}>{item.name}</div>
                    <div style={{ fontSize: '0.5rem', color: rarityColor, margin: '4px 0', textTransform: 'uppercase', fontWeight: 'bold' }}>{formatRarityName(item.rarity)}</div>
                  </div>
                  <div style={{ fontSize: '0.55rem', color: '#fff', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', fontStyle: 'italic', minHeight: '3em' }}>
                    {item.description || "No data available."}
                  </div>
{owned && (
  <button 
    onClick={() => handleSellTransition(item, owned)}
    style={{ 
      marginTop: '12px', 
      background: 'rgba(0, 0, 0, 0.4)', 
      border: '1px solid #333', 
      color: '#888', 
      fontSize: '0.5rem', 
      padding: '4px 8px', 
      cursor: 'pointer', 
      textTransform: 'uppercase', 
      fontWeight: '500',
      letterSpacing: '0.5px',
      transition: 'all 0.15s ease-in-out',
      width: 'fit-content',
      alignSelf: 'center'
    }}
    onMouseOver={(e) => { 
      e.currentTarget.style.borderColor = rarityColor; 
      e.currentTarget.style.color = rarityColor;
      e.currentTarget.style.boxShadow = `0 0 6px ${rarityColor}44`;
    }}
    onMouseOut={(e) => { 
      e.currentTarget.style.borderColor = '#333'; 
      e.currentTarget.style.color = '#888'; 
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
     SELL 
  </button>
)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}